import React from "react";
import { Routes, Route, NavLink, Link, Navigate } from "react-router-dom";
import { ClipboardCheck, ShieldCheck, FolderCheck, FileText, ArrowLeft } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import ThemeToggle from "../components/ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

import ComplianceAudits from "../components/compliance/ComplianceAudits";
import ComplianceControls from "../components/compliance/ComplianceControls";
import ComplianceEvidence from "../components/compliance/ComplianceEvidence";
import CompliancePolicies from "../components/compliance/CompliancePolicies";

const TABS = [
  { to: "/auditor", label: "Audits", icon: ClipboardCheck, end: true },
  { to: "/auditor/controls", label: "Controls", icon: ShieldCheck },
  { to: "/auditor/evidence", label: "Evidence", icon: FolderCheck },
  { to: "/auditor/policies", label: "Policies", icon: FileText },
];

export default function AuditorPortal() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const isAuditor = user?.role === "Auditor";

  if (!isAdmin && !isAuditor) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="auditor-portal-shell">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <BrandLogo variant="full" className="h-12 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-border" />
            <span className="hidden sm:inline text-[13px] font-semibold text-muted-foreground tracking-wide">Auditor Portal</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-border text-[13px] font-medium hover:bg-muted transition-colors"
              data-testid="auditor-portal-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to UC Vigil
            </Link>
          </div>
        </div>
        <nav className="mx-auto max-w-[1200px] px-4 sm:px-6 flex gap-1 overflow-x-auto" data-testid="auditor-portal-nav">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`auditor-tab-${t.label.toLowerCase()}`}
              className={({ isActive }) => cn(
                "flex items-center gap-1.5 px-3.5 h-11 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors",
                isActive ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1200px] px-4 sm:px-6 py-6">
        <p className="text-[12.5px] text-muted-foreground mb-4" data-testid="auditor-portal-readonly-note">
          Read-only access for audit fieldwork — you can view audits, controls, evidence and
          policies, and raise or comment on findings.
        </p>
        <Routes>
          <Route index element={<ComplianceAudits isAdmin={false} canEditFindings />} />
          <Route path="controls" element={<ComplianceControls isAdmin={false} />} />
          <Route path="evidence" element={<ComplianceEvidence isAdmin={false} />} />
          <Route path="policies" element={<CompliancePolicies isAdmin={false} />} />
        </Routes>
      </main>

      <footer className="mx-auto max-w-[1200px] px-4 sm:px-6 py-8 text-[12px] text-muted-foreground">
        Signed in as {user?.name} ({user?.email})
      </footer>
    </div>
  );
}
